import { useState } from "react";
import { Field, Label } from "@headlessui/react";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalForm from "../../components/ModalForm";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";

export const passwordInputClassName =
	"block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6";

/**
 * Turns a failed mutation into the message PasswordConfirmDialog shows, and always clears the
 * request (and with it the password) from the Redux store afterwards.
 */
export function submitAndReset(request: Promise<void>, reset: () => void): Promise<void> {
	return request
		.catch((e: any) => {
			throw typeof e?.data === "string" ? e.data : "The change failed.";
		})
		.finally(() => reset());
}

interface Props {
	title: string;
	message: React.ReactNode;
	confirmLabel: string;
	onSubmit: (password: string) => Promise<void>;
	onClose: () => void;
}

export default function PasswordConfirmDialog({
	title,
	message,
	confirmLabel,
	onSubmit,
	onClose,
}: Props) {
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	function handleConfirm() {
		setError("");
		setIsSubmitting(true);
		onSubmit(password)
			.then(() => onClose())
			.catch((message: string) => {
				setPassword("");
				setError(message);
			})
			.finally(() => setIsSubmitting(false));
	}

	return (
		<ModalForm
			open
			title={title}
			form={
				<div className="pt-2 space-y-4">
					<AlertMessage
						type={AlertLevel.Error}
						message={error}
						showIcon
						onClear={() => setError("")}
					/>
					<div className="text-sm text-gray-600 mb-2">{message}</div>
					<Field>
						<Label htmlFor="secrets-your-password" className="font-medium text-sm">
							Enter Your Password
							<RequiredField />
						</Label>
						<Password
							id="secrets-your-password"
							name="secrets-your-password"
							placeholder="your password"
							autoComplete="new-password"
							className={passwordInputClassName}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</Field>
				</div>
			}
			confirmLabel={confirmLabel}
			disableConfirm={!password || isSubmitting}
			onConfirm={handleConfirm}
			onCancel={onClose}
		/>
	);
}
